import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { routes } from '@platform/core/lib/routes';
import { RecordLayout } from '../RecordLayout';
import { useDraftValidator } from '../useDraftValidator';
import { useRecordGuard } from '../useRecordGuard';
import type { INewRecordViewProps } from './INewRecordViewProps';
import { NEW_RECORD_ID } from './newRecordId';
import { useNewRecordEditor } from './useNewRecordEditor';

/**
 * Повна форма нового запису (прототип v4: «+ Створити …» відкриває запис у режимі редагування): та сама сторінка запису з типовими
 * значеннями; «Зберегти» перевіряє обов'язкові поля, створює запис і відкриває його сторінку (замість адреси чернетки), «Скасувати»
 * відкидає чернетку й повертає туди, звідки прийшли; вихід з незбереженими змінами питає підтвердження. Вкладки — без адреси.
 * У розділеному вигляді (`onDone`) переходу немає: панель отримує створений запис або відмову.
 */
export function NewRecordView({ ws, module, rt, record, from, variant = 'page', onDone }: INewRecordViewProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [tab, setTab] = useState('');
  const validate = useDraftValidator(rt, record, true);
  const { editor, created, cancelled } = useNewRecordEditor(ws, rt.key, record, { validate });
  const guard = useRecordGuard(editor, onDone ? pathname : routes.record(module.module.key, rt.key, NEW_RECORD_ID));
  const done = useRef(false);
  const release = guard.release;
  useEffect(() => {
    if (done.current || (!created && !cancelled)) return;
    done.current = true;
    release();
    if (onDone) onDone(created?.id ?? null);
    else if (created) navigate(routes.record(module.module.key, rt.key, created.id), { replace: true });
    else if (from) navigate(-1);
    else navigate(routes.list(module.module.key, rt.key), { replace: true });
  }, [created, cancelled, release, navigate, ws, module.module.key, rt.key, from, onDone]);
  return <RecordLayout ws={ws} module={module} rt={rt} record={record} tab={tab} onTab={setTab} variant={variant} editor={editor} guard={guard} isNew />;
}
